<template>
    <div class="light-grad-news"></div>
    <template v-if="everythingLoaded">
        <div class="io-container tw-relative tw-z-[1] tw-mt-14 tw-flex tw-flex-col tw-gap-28">
            <section class="tw-text-center">
                <template v-if="page">
                    <h1 class="tw-mb-4 tw-text-2xl lg:tw-text-4xl" v-html="page.title"></h1>
                    <h3 class="tw-mb-8 tw-text-base tw-font-medium lg:tw-text-lg" v-html="page.subTitle"></h3>
                    <div class="tw-mx-auto tw-mb-16 tw-flex tw-w-full tw-max-w-[400px] tw-flex-col tw-items-center tw-gap-4 lg:tw-max-w-[500px] lg:tw-flex-row lg:tw-gap-0">
                        <v-text-field
                            v-model="search"
                            class="news-search-input tw-h-12 tw-w-full tw-pr-0 lg:tw-h-10"
                            flat
                            :placeholder="page.inputPlaceholder"
                            :readonly="isLoading"
                            rounded="pill"
                            type="text"
                            variant="solo"
                            @keyup.enter="onSearch()">
                            <template #append-inner>
                                <v-btn
                                    class="tw-h-full tw-rounded-l-none max-lg:tw-w-min"
                                    color="primary"
                                    elevation="0"
                                    flat
                                    rounded="pill"
                                    @click="onSearch()">
                                    <span class="tw-text-sm tw-font-light max-lg:tw-hidden lg:tw-text-sm"> {{ page.buttonText }} </span>
                                    <v-icon class="tw-text-xl lg:tw-hidden">mdi-magnify</v-icon>
                                </v-btn>
                            </template>
                        </v-text-field>
                    </div>
                </template>
                <div class="tw-flex tw-flex-wrap tw-justify-start tw-gap-4 lg:tw-justify-center">
                    <v-btn
                        v-for="tag in tags"
                        :key="tag.id"
                        color="blue"
                        elevation="0"
                        rounded="pill"
                        :variant="selectedTag == tag.slug ? 'flat' : 'outlined'"
                        @click="selectTag(tag.slug)">
                        <span class="tw-text-sm">{{ tag.name }}</span>
                    </v-btn>
                </div>
                <v-progress-circular
                    v-if="isLoading"
                    class="tw-ml-auto tw-mt-16"
                    color="primary"
                    indeterminate></v-progress-circular>
                <div class="tw-mt-16 tw-grid tw-grid-cols-1 tw-gap-x-8 tw-gap-y-16 sm:tw-grid-cols-2 lg:tw-grid-cols-3 3xl:tw-grid-cols-4">
                    <NewsCard
                        v-for="i in news"
                        :key="i.id"
                        :news="i" />
                </div>
                <div v-if="news.length === 0 && !isLoading">
                    <h2 class="tw-mb-4 tw-text-lg lg:tw-text-2xl">{{ $t('news.noNewsFound') }}</h2>
                    <h3 class="tw-text-sm tw-font-medium lg:tw-text-base">{{ $t('news.tryAnotherSearchOrTag') }}</h3>
                </div>
                <span v-intersect="onPageEnd"></span>
            </section>
        </div>
    </template>
    <template v-else>
        <!-- loader -->
        <Loader />
    </template>
</template>

<script setup lang="ts">
    import axios from '@/plugins/axios';
    import qs from 'qs';
    import Loader from '@/components/Loader.vue';
    import _ from 'lodash';
    import { useAppStore } from '@/store/app';
    import { useI18n } from 'vue-i18n';
    const appStore = useAppStore();
    const { t } = useI18n();

    const $router = useRouter();
    const $route = useRoute();

    const selectedTag = ref<string | null>(null);
    const tags = ref([
        {
            id: null,
            name: t('news.allTags'),
            slug: null,
        },
    ]);

    const hasLoaded = ref(false);
    const isLoading = ref(false);
    const news = ref([]);
    const currentPage = ref(1);
    const totalPages = ref(0);
    const search = ref('');

    const page = computed(() => appStore.getNewsAllPage);

    const everythingLoaded = computed(() => {
        return hasLoaded.value && appStore.hasFinishedLoading;
    });

    function onSearch() {
        currentPage.value = 1;
        fetchNews();
    }

    function selectTag(tag: string) {
        selectedTag.value = tag;
        currentPage.value = 1;
        fetchNews();
        $router.push({ query: { tag: tag || null } });
    }

    function onPageEnd(isIntersecting: boolean) {
        if (isIntersecting && currentPage.value < totalPages.value) {
            currentPage.value++;
            fetchNews();
        }
    }

    async function fetchNews() {
        isLoading.value = true;
        try {
            const query = {
                sort: ['publishDate:desc'],
                populate: ['image', 'tags'],
                pagination: {
                    pageSize: 16,
                    page: currentPage.value,
                },
            };

            if (search.value && selectedTag.value) {
                _.set(query, 'filters.title.$containsi', search.value);
                _.set(query, 'filters.$or[0].tags.slug', selectedTag.value);
                _.set(query, 'filters.$or[1].mainTag.slug', selectedTag.value);
            } else if (selectedTag.value) {
                _.set(query, 'filters.$or[0].tags.slug', selectedTag.value);
                _.set(query, 'filters.$or[1].mainTag.slug', selectedTag.value);
            } else if (search.value) {
                _.set(query, 'filters.$or[0].title.$containsi', search.value);
                _.set(query, 'filters.$or[1].tags.slug.$containsi', search.value);
                _.set(query, 'filters.$or[2].mainTag.slug.$containsi', search.value);
            }

            const { data } = await axios.get(`/news?${qs.stringify(query)}`);
            if (currentPage.value === 1) {
                news.value = [...data.data];
            } else {
                news.value = [...news.value, ...data.data];
            }

            totalPages.value = data.meta.pagination.pageCount;
        } catch (error) {
            console.error('Could not fetch news', error);
        } finally {
            isLoading.value = false;
        }
    }

    // fetch tags with query params
    async function fetchTags() {
        try {
            const query = {
                sort: ['name:asc'],
                filters: {
                    news: {
                        $notNull: true,
                    },
                },
                pagination: {
                    pageSize: 100,
                },
            };
            const { data } = await axios.get(`/tags?${qs.stringify(query)}`);
            tags.value = [...tags.value, ...data.data];
        } catch (error) {
            console.error('Could not fetch tags', error);
        }
    }

    async function init() {
        const query = $route.query;
        if (query.tag) {
            selectedTag.value = query.tag as string;
        }

        await Promise.all([fetchTags(), fetchNews()]);

        hasLoaded.value = true;
    }

    init();

    document.title = t('news.docTitle');
</script>

<style scoped></style>
